import { BankOutlined } from '@ant-design/icons';
import { FormGroup } from 'components';
import { FormGroupItem } from 'components/FormGroupItem';
import React, {
  Fragment,
  FunctionComponent,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { isAdminSelector } from 'features/user/redux/selectors';
import { styled } from 'theme';
import type { Firebase } from 'types/firebase';
import { FormFieldType } from '..';
import * as Styles from '../MerchantDetailsForm/MerchantDetailsForm.styles';
import { useSelector } from 'react-redux';
import {
  accountSelector,
  contractOptionsSelector,
} from 'features/merchants/redux';

interface MerchantAcquirerForm {
  merchant: Firebase.Merchant;
  onSave: (
    inputName: string,
    newAcquirerMerchantId: string | number,
    forced: boolean,
  ) => void;
}

/**
 * Styling
 */
export const Root = styled.div`
  margin-bottom: 64px;
`;

const MerchantAcquirerForm: FunctionComponent<MerchantAcquirerForm> = ({
  merchant,
  onSave,
}) => {
  const account = useSelector(accountSelector(merchant.accountId));

  const [forcingClearhausMerchantId, setForcingClearhausMerchantId] = useState(
    false,
  );

  useEffect(() => {
    if (merchant.clearhausKYCdone) {
      setForcingClearhausMerchantId(false);
    }
  }, [merchant.clearhausKYCdone]);

  const handleClearhausMerchantId = useCallback(
    () => setForcingClearhausMerchantId(true),
    [],
  );
  const isAdmin = useSelector(isAdminSelector);
  /**
   * The contract list, from the `contracts` collection managed on the v2 dashboard's
   * /partners page (DEV-2664). Falls back to the hardcoded CONTRACT_OPTIONS while that
   * collection is empty, so nothing changes here until somebody imports it.
   */
  const contractOptions = useSelector(contractOptionsSelector);

  const alertDescription = useMemo(
    () => (
      <Fragment>
        It seems the Clearhaus merchant id is not correct,
        <span
          onClick={handleClearhausMerchantId}
          style={{ cursor: 'pointer', textDecoration: 'underline' }}
        >
          click here if the Clearhaus Merchant Id is correct
        </span>
      </Fragment>
    ),
    [handleClearhausMerchantId],
  );

  const merchantApproved = useCallback((): boolean => {
    return merchant.clearhausKYCdone ? true : false;
  }, [merchant.clearhausKYCdone]);

  const cannotChangeMerchantClearhausId = useCallback((): boolean => {
    //Check if every field is truthy
    return ![
      merchant?.swift,
      merchant?.iban,
      merchant?.mcc,
      merchant?.merchantContract,
      account?.partnerId,
    ].every(Boolean);
  }, [
    merchant?.swift,
    merchant?.iban,
    merchant?.mcc,
    merchant?.merchantContract,
    account?.partnerId,
  ]);

  const cantEditChID = cannotChangeMerchantClearhausId() || merchantApproved();

  const formFields = useMemo<Array<FormFieldType>>(
    () => [
      {
        title: 'Acquirer',
        icon: <BankOutlined />,
        inputs: [
          {
            label: 'Clearhaus Merchant Id',
            alternativeLabel: 'Change Clearhaus Merchant Id',
            name: 'clearhausMerchantId',
            value: merchant?.clearhausMerchantId,
            isValid: (value: string | number) => {
              const lengthSeven = Boolean(
                value.toString().trim()?.length === 7,
              );
              const isANumber = !isNaN(Number(value.toString()));
              return forcingClearhausMerchantId || (lengthSeven && isANumber);
            },
            alertDescription,
            restrictEdit: cantEditChID,
          },
          {
            label: 'MCC',
            alternativeLabel: 'Change MCC',
            name: 'mcc',
            value: merchant.mcc,
            isValid: (value: string | number) => {
              const lengthFour = Boolean(value.toString().trim()?.length === 4);
              const isANumber = !isNaN(Number(value.toString()));
              return lengthFour && isANumber;
            },
          },
          {
            label: 'Merchant Contract',
            alternativeLabel: 'Change Merchant Contract',
            name: 'merchantContract',
            value: merchant.merchantContract,
            type: 'dropdown',
            restrictEdit: isAdmin ? false : merchantApproved(),
            dropdown: {
              placeholder: 'Select a contract',
              values: contractOptions,
              defaultValue: 'softpos_q3_2022_dk',
              options: contractOptions,
            },
          },
        ],
      },
    ],
    [
      alertDescription,
      forcingClearhausMerchantId,
      merchant.clearhausMerchantId,
      merchant.mcc,
      merchant.iban,
      merchant.swift,
      merchant.merchantContract,
      merchant.clearhausKYCdone,
      cantEditChID,
      // The collection arrives after first render — without this the dropdown keeps
      // showing whatever the fallback returned.
      contractOptions,
    ],
  );

  return (
    <div>
      {formFields.map((form) => (
        <Styles.FormContainer key={form.title}>
          <FormGroup icon={form.icon} title={form.title}>
            {form.inputs.map((input) => (
              <FormGroupItem
                key={input.name}
                {...input}
                onSave={(newAcquirerMerchantId) => {
                  onSave(
                    input.name,
                    newAcquirerMerchantId,
                    forcingClearhausMerchantId,
                  );
                  setForcingClearhausMerchantId(false);
                }}
              />
            ))}
          </FormGroup>
        </Styles.FormContainer>
      ))}
    </div>
  );
};

export { MerchantAcquirerForm };
